import React, { useState } from 'react';
import { ImageIcon, Camera, ChevronLeft, ChevronRight, Trash2, Plus, Receipt } from 'lucide-react';
import ImagePreviewModal from './ImagePreviewModal';
import { convertToProxyUrl } from '../../utils/imageUrl';

const ImageGallery = ({
  artworkPhotos = [],
  classroomPhotos = [],
  paymentProofPhotos = [],
  editable = false,
  onRemove = null,
  onAdd = null,
  maxFiles = 0,
  showThumbnails = true,
  showNavigationArrows = true,
  hideOnError = false,
  thumbnailSize = 'normal' // 'normal' | 'small' - small 模式缩略图更小，显示更多列
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedImages, setFailedImages] = useState(new Set()); // 加载失败的图片

  // 触摸滑动状态
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // 合并所有图片，并标记类型，同时转换内网 URL 为代理 URL
  const allImages = [
    ...(Array.isArray(artworkPhotos) ? artworkPhotos : []).map((url, index) => ({
      url: convertToProxyUrl(url),
      originalUrl: url,
      type: 'artwork',
      alt: `作品图片 ${index + 1}`,
      icon: ImageIcon,
      badge: '作品',
      originalIndex: index
    })),
    ...(Array.isArray(classroomPhotos) ? classroomPhotos : []).map((url, index) => ({
      url: convertToProxyUrl(url),
      originalUrl: url,
      type: 'classroom',
      alt: `课堂照片 ${index + 1}`,
      icon: Camera,
      badge: '课堂',
      originalIndex: index + (Array.isArray(artworkPhotos) ? artworkPhotos : []).length
    })),
    ...(Array.isArray(paymentProofPhotos) ? paymentProofPhotos : []).map((url, index) => ({
      url: convertToProxyUrl(url),
      originalUrl: url,
      type: 'payment',
      alt: `支付凭证 ${index + 1}`,
      icon: Receipt,
      badge: '凭证',
      originalIndex: index + (Array.isArray(artworkPhotos) ? artworkPhotos : []).length + (Array.isArray(classroomPhotos) ? classroomPhotos : []).length
    }))
  ];

  // 根据 hideOnError 过滤掉加载失败的图片
  const displayImages = hideOnError
    ? allImages.filter(img => !failedImages.has(img.url))
    : allImages;

  const canAdd = editable && onAdd && (maxFiles === 0 || allImages.length < maxFiles);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  const handleImageClick = (index) => {
    setCurrentIndex(index);
  };

  // 触摸滑动处理 - 最小滑动距离
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && displayImages.length > 1) {
      // 左滑 -> 下一张
      setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
    }
    if (isRightSwipe && displayImages.length > 1) {
      // 右滑 -> 上一张
      setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
    }
  };

  if (displayImages.length === 0) {
    return (
      <div className="bg-white p-8 rounded-xl border border-[#EDF2F7] shadow-sm">
        <div className="text-center text-gray-400 flex flex-col items-center justify-center">
          <ImageIcon size={48} className="mx-auto mb-3 opacity-20" />
          <span className="text-sm">暂无图片</span>
        </div>
      </div>
    );
  }

  // 确保 currentIndex 在有效范围内
  const safeIndex = Math.min(currentIndex, displayImages.length - 1);

  return (
    <div className="space-y-4">
      {/* 主图展示区 */}
      <div className="bg-white overflow-hidden">
        <div className="relative">
          {/* 主图 */}
          <div
            className="bg-gray-50 flex items-center justify-center"
            style={{ height: '400px' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={displayImages[safeIndex].url}
              alt={displayImages[safeIndex].alt}
              className="max-h-full max-w-full object-contain cursor-pointer select-none rounded-xl"
              onClick={() => setPreviewImage(displayImages[safeIndex])}
              draggable={false}
              onError={(e) => {
                const failedUrl = displayImages[safeIndex].url;
                console.error('图片加载失败:', {
                  url: failedUrl,
                  originalUrl: displayImages[safeIndex].originalUrl,
                  currentIndex: safeIndex,
                  error: e
                });
                if (hideOnError) {
                  // 记录失败的图片，触发重新渲染
                  setFailedImages(prev => new Set([...prev, failedUrl]));
                } else {
                  // 原有逻辑：显示失败占位符
                  e.target.src = '';
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
            {!hideOnError && (
              <div className="hidden h-full w-full items-center justify-center text-gray-400">
                <div className="text-center">
                  <ImageIcon size={48} className="mx-auto mb-2 opacity-20" />
                  <span className="text-xs">图片加载失败</span>
                </div>
              </div>
            )}
          </div>

          {/* 左右切换按钮 */}
          {showNavigationArrows && displayImages.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
              >
                <ChevronLeft size={20} className="text-gray-700" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
              >
                <ChevronRight size={20} className="text-gray-700" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* 缩略图网格 */}
      {showThumbnails && (
        <div className={`bg-white border border-[#EDF2F7] shadow-sm ${thumbnailSize === 'small' ? 'p-2' : 'p-4'}`}>
          <div className={`grid ${thumbnailSize === 'small' ? 'gap-1 grid-cols-7 sm:grid-cols-8' : 'gap-3 grid-cols-3 sm:grid-cols-4'}`}>
            {allImages.map((image, index) => {
              const Icon = image.icon;
              return (
                <div
                  key={index}
                  onClick={() => handleImageClick(index)}
                  className={`group relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${currentIndex === index
                    ? 'ring-2 ring-[#3182CE] ring-offset-2'
                    : 'hover:ring-2 hover:ring-gray-300'
                    }`}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('缩略图加载失败:', image.url);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full bg-gray-100 items-center justify-center">
                    <Icon size={16} className="text-gray-400 opacity-50" />
                  </div>

                  {/* 删除按钮 - 编辑模式，右上角外侧 */}
                  {editable && onRemove && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(image.originalIndex);
                      }}
                      className="absolute -top-2 -right-2 p-2 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg z-10"
                      title="删除图片"
                    >
                      <Trash2 size={14} className="text-white" />
                    </button>
                  )}
                </div>
              );
            })}

            {/* 添加按钮 - 编辑模式 */}
            {canAdd && (
              <div
                onClick={onAdd}
                className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer flex flex-col items-center justify-center transition-all group"
              >
                <Plus size={24} className="text-gray-400 group-hover:text-blue-500 transition-colors mb-1" />
                <span className="text-xs text-gray-500 group-hover:text-blue-600 transition-colors">添加</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 图片预览模态框 */}
      {previewImage && (
        <ImagePreviewModal
          imageUrl={previewImage.url}
          altText={previewImage.alt}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

export default ImageGallery;
